"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePeriodeStore } from "@/hooks/use-periode";
import { useMasterPeriode } from "@/hooks/use-master-periode";
import { fetchMasterPeriodeKelas } from "./api/get-master-periode-kelas";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChevronDown,
  ChevronRight,
  GraduationCap,
  School,
  Loader2,
  Users,
} from "lucide-react";

export default function PeriodeKelasPage() {
  const { activePeriod } = usePeriodeStore();
  const { data: masterPeriodes } = useMasterPeriode();

  const currentPeriodObj = masterPeriodes?.find(
    (p) => `${p.tahun}-${p.semester}` === activePeriod,
  );
  const master_periode_id = currentPeriodObj?.master_periode_id;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["periode-kelas", master_periode_id],
    queryFn: () => fetchMasterPeriodeKelas(master_periode_id!),
    enabled: !!master_periode_id,
  });

  const [expandedFakultas, setExpandedFakultas] = useState<
    Record<string, boolean>
  >({});

  const toggleFakultas = (kode: string) => {
    setExpandedFakultas((prev) => ({
      ...prev,
      [kode]: !prev[kode],
    }));
  };

  const fakultasList = data?.datas || [];

  return (
    <div className="space-y-6">
      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-slate-50/80 border-b border-slate-100 pb-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-1.5">
              <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">
                Kelas Fakultas & Prodi
              </CardTitle>
              <CardDescription className="text-slate-500 text-sm">
                Manajemen data kelas per fakultas dan prodi.
                <br />
                <span className="font-semibold text-blue-600">
                  Periode:{" "}
                  {currentPeriodObj
                    ? `${currentPeriodObj.semester === 1 ? "Ganjil" : "Genap"} ${currentPeriodObj.tahun}/${parseInt(currentPeriodObj.tahun) + 1}`
                    : "..."}
                </span>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400 space-y-3">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <p className="text-sm">Memuat data kelas...</p>
            </div>
          ) : isError ? (
            <div className="py-12 text-center text-red-500 font-medium text-sm">
              Gagal memuat data dari server.
            </div>
          ) : fakultasList.length === 0 ? (
            <div className="py-12 text-center text-slate-500 text-sm">
              Tidak ada data kelas fakultas yang ditemukan.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {fakultasList.map((fakultas) => {
                const isExpanded = !!expandedFakultas[fakultas.kode_fakultas];
                return (
                  <div key={fakultas.kode_fakultas} className="flex flex-col">
                    {/* Fakultas Row */}
                    <div
                      className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${
                        isExpanded ? "bg-slate-50" : "hover:bg-slate-50/50"
                      }`}
                      onClick={() => toggleFakultas(fakultas.kode_fakultas)}
                    >
                      <button className="text-slate-500 hover:text-slate-800 transition-colors p-1 rounded-md hover:bg-slate-200">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                      </button>
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 text-blue-600 shrink-0">
                        <School className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-800 text-base">
                          {fakultas.nama_fakultas}
                        </h4>
                        <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                          <span className="flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" />
                            {fakultas.total_mhs_fakultas} Mahasiswa
                          </span>
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium border border-blue-100">
                            Kelas {fakultas.kelas_fakultas}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Prodi List (Expanded) */}
                    {isExpanded && (
                      <div className="bg-slate-50/50 border-t border-slate-100 p-4 pl-18">
                        <div className="space-y-3">
                          {fakultas.prodi && fakultas.prodi.length > 0 ? (
                            fakultas.prodi.map((prodi) => (
                              <div
                                key={prodi.kode_unit}
                                className="flex items-center justify-between p-3 rounded-lg bg-white border border-slate-200 shadow-sm"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 shrink-0">
                                    <GraduationCap className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-800 text-sm">
                                      {prodi.nama_prodi}
                                    </p>
                                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                      <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-medium">
                                        {prodi.program}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Users className="w-3.5 h-3.5" />
                                        {prodi.jumlah_mhs} Mhs
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="shrink-0 flex flex-col items-end">
                                  <span className="text-xs text-slate-500 mb-1">
                                    Kelas
                                  </span>
                                  <span className="bg-indigo-100 text-indigo-700 font-bold px-3 py-1 rounded-md text-sm border border-indigo-200">
                                    {prodi.kelas}
                                  </span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-slate-500 py-2">
                              Tidak ada data prodi
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
